import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFiscalYear } from '../context/FiscalYearContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { DateInput } from '../components/ui/date-input';
import AccountSelector from '../components/selectors/AccountSelector';
import InventorySelector from '../components/selectors/InventorySelector';
import QuickAddAccountDialog from '../components/dialogs/QuickAddAccountDialog';
import QuickAddInventoryDialog from '../components/dialogs/QuickAddInventoryDialog';
import FishingTripPrint from '../components/invoice/FishingTripPrint';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  FileText, Search, Plus, Edit, Trash2, Send, Eye, Printer,
  Filter, Undo2, X, Save, Download
} from 'lucide-react';
import axios from 'axios';
import { formatUSD, formatDate } from '../lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 20;

const SalesInvoicePage = () => {
  const { user, currentOrg, canEdit, isVatRegistered } = useAuth();
  const { selectedFY } = useFiscalYear();
  
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [showForm, setShowForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [qaCustomerOpen, setQaCustomerOpen] = useState(false);
  const [qaCustomerMode, setQaCustomerMode] = useState('add');
  const [qaCustomerEdit, setQaCustomerEdit] = useState(null);
  const [qaItemOpen, setQaItemOpen] = useState(false);
  const [qaItemMode, setQaItemMode] = useState('add');
  const [qaItemEdit, setQaItemEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);
  
  const [salesAccounts, setSalesAccounts] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [calibres, setCalibres] = useState([]);
  const [currencies] = useState([
    { code: 'EUR', name: 'Euro', symbol: '€' },
        { code: 'XOF', name: 'West African CFA Franc', symbol: 'FCFA' }
  ]);
  const [exchangeRate, setExchangeRate] = useState(655.957);

  const fmt = (n) => (Number(n) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 });

  const emptyLine = {
    inventory_item_id: '', item_name: '', calibre_name: '',
    quantity: '', unit: 'kg', unit_price: '', cost: '', currency: 'XOF',
    exchange_rate: 655.957, discount_percent: 0, line_total: 0, line_total_usd: 0,
    is_taxable: false
  };
  
  const defaultFormData = {
    date: new Date().toISOString().split('T')[0],
    due_date: '',
    lines: [{ ...emptyLine }],
    subtotal: 0, discount_percent: 0, discount_amount: 0,
    tax_percent: 0, tax_amount: 0, total: 0, total_usd: 0,
    currency: 'XOF', notes: '',
    debit_account_id: '', credit_account_id: '',
    organization_id: currentOrg?.id || ''
  };
  
  const [formData, setFormData] = useState(defaultFormData);
  
  useEffect(() => {
    if (currentOrg) {
      fetchData();
      fetchInvoices(true);
    }
  }, [currentOrg, selectedFY]);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentOrg) fetchInvoices(true);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, filterStatus]);
  
  const fetchData = async () => {
    try {
      const [salesRes, inventoryRes, rateRes, calibresRes] = await Promise.all([
        axios.get(`${API}/sales-accounts?organization_id=${currentOrg.id}`),
        axios.get(`${API}/inventory?organization_id=${currentOrg.id}&page_size=1000`),
        axios.get(`${API}/exchange-rates/latest?organization_id=${currentOrg.id}`).catch(() => ({ data: { rate: 655.957 } })),
        axios.get(`${API}/calibres?organization_id=${currentOrg.id}`).catch(() => ({ data: [] })),
      ]);
      setSalesAccounts(salesRes.data);
      const items = inventoryRes.data?.items || inventoryRes.data || [];
      setInventoryItems(Array.isArray(items) ? items : []);
      setCalibres(Array.isArray(calibresRes.data) ? calibresRes.data : []);
      if (rateRes.data?.rate) setExchangeRate(rateRes.data.rate);
    } catch (error) {
      console.error('Failed to fetch reference data:', error);
    }
  };
  
  const fetchInvoices = async (reset = false) => {
    if (reset) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = new URLSearchParams({
        organization_id: currentOrg.id,
        skip: reset ? 0 : currentPage * PAGE_SIZE,
        limit: PAGE_SIZE
      });
      if (searchTerm) params.append('search', searchTerm);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (selectedFY) {
        params.append('date_from', selectedFY.start_date);
        params.append('date_to', selectedFY.end_date);
      }
      const [invoicesRes, countRes] = await Promise.all([
        axios.get(`${API}/sales-invoices?${params.toString()}`),
        axios.get(`${API}/sales-invoices/count?organization_id=${currentOrg.id}${filterStatus !== 'all' ? `&status=${filterStatus}` : ''}${searchTerm ? `&search=${searchTerm}` : ''}`)
      ]);
      if (reset) {
        setInvoices(invoicesRes.data);
        setCurrentPage(1);
      } else {
        setInvoices(prev => [...prev, ...invoicesRes.data]);
        setCurrentPage(prev => prev + 1);
      }
      setTotalCount(countRes.data.total || countRes.data.count || 0);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };
  
  const hasMore = invoices.length < totalCount;
  
  const calculateLineTotal = (line) => {
    const poids = parseFloat(line.quantity) || 0;
    const prix = parseFloat(line.unit_price) || 0;
    const rate = parseFloat(exchangeRate) || 655.957;
    const lineTotal = prix * poids;               // XOF
    const lineTotalUsd = lineTotal / rate;         // EUR (internal base for accounting)
    return { lineTotal, lineTotalUsd };
  };

  const recalculateTotals = (lines) => {
    const totalXof = lines.reduce((sum, l) => sum + (parseFloat(l.line_total) || 0), 0);
    const rate = parseFloat(exchangeRate) || 655.957;
    const totalUsd = totalXof / rate;
    return { subtotal: totalUsd, discountAmount: 0, taxAmount: 0, total: totalXof, totalUsd };
  };

  const applyLineRecalc = (newLines) => {
    const totals = recalculateTotals(newLines);
    setFormData(prev => ({
      ...prev, lines: newLines,
      subtotal: totals.subtotal, discount_amount: 0, tax_amount: 0,
      discount_percent: 0, tax_percent: 0,
      total: totals.total, total_usd: totals.totalUsd, currency: 'XOF',
    }));
  };

  // Fish type selection from inventory
  const handleFishSelect = (index, itemId) => {
    const newLines = [...formData.lines];
    const item = inventoryItems.find(i => i.id === itemId);
    newLines[index] = {
      ...newLines[index],
      inventory_item_id: itemId,
      item_name: item ? item.name : newLines[index].item_name,
      unit: 'kg', currency: 'XOF', exchange_rate: exchangeRate,
    };
    const { lineTotal, lineTotalUsd } = calculateLineTotal(newLines[index]);
    newLines[index].line_total = lineTotal;
    newLines[index].line_total_usd = lineTotalUsd;
    applyLineRecalc(newLines);
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index] = { ...newLines[index], [field]: value };
    const { lineTotal, lineTotalUsd } = calculateLineTotal(newLines[index]);
    newLines[index].line_total = lineTotal;
    newLines[index].line_total_usd = lineTotalUsd;
    applyLineRecalc(newLines);
  };
  
  const addLine = () => {
    setFormData({ ...formData, lines: [...formData.lines, { ...emptyLine, exchange_rate: exchangeRate }] });
  };

  const seedAllFish = () => {
    if (!inventoryItems.length) { toast.error('No fish types found in Inventory'); return; }
    const seeded = inventoryItems.map(f => ({
      ...emptyLine, exchange_rate: exchangeRate,
      inventory_item_id: f.id, item_name: f.name,
    }));
    applyLineRecalc(seeded);
    toast.success(`${seeded.length} fish types added`);
  };
  
  const removeLine = (index) => {
    if (formData.lines.length <= 1) return;
    const newLines = formData.lines.filter((_, i) => i !== index);
    applyLineRecalc(newLines);
  };
  
  const openCreateForm = () => {
    setEditingInvoice(null);
    setFormData({ ...defaultFormData, organization_id: currentOrg.id });
    setShowForm(true);
  };
  
  const openEditForm = (inv) => {
    if (inv.is_posted) { toast.error('Cannot edit posted invoice'); return; }
    setEditingInvoice(inv);
    setFormData({
      date: inv.date, due_date: inv.due_date || '',
      lines: inv.lines.map(l => ({ ...emptyLine, ...l })),
      subtotal: inv.subtotal, discount_percent: inv.discount_percent || 0,
      discount_amount: inv.discount_amount || 0, tax_percent: inv.tax_percent || 0,
      tax_amount: inv.tax_amount || 0, total: inv.total, total_usd: inv.total_usd,
      currency: inv.currency || 'EUR', notes: inv.notes || '',
      debit_account_id: inv.debit_account_id, credit_account_id: inv.credit_account_id,
      organization_id: currentOrg.id
    });
    setShowForm(true);
  };
  
  const handleSave = async () => {
    if (!formData.debit_account_id || !formData.credit_account_id) {
      toast.error('Please select both Customer and Sales accounts');
      return;
    }
    const rate = parseFloat(exchangeRate) || 655.957;
    // Only keep lines with a fish name and quantity (Poids) > 0
    const keptLines = formData.lines
      .filter(l => l.item_name && (parseFloat(l.quantity) || 0) > 0)
      .map(l => {
        const poids = parseFloat(l.quantity) || 0;
        const prix = parseFloat(l.unit_price) || 0;
        const lineTotal = prix * poids;
        return {
          inventory_item_id: l.inventory_item_id || null,
          item_name: l.item_name,
          calibre_name: l.calibre_name || null,
          quantity: poids,
          unit: 'kg',
          unit_price: prix,
          cost: parseFloat(l.cost) || 0,
          currency: 'XOF',
          exchange_rate: rate,
          discount_percent: 0,
          line_total: lineTotal,
          line_total_usd: lineTotal / rate,
          is_taxable: false,
        };
      });
    if (keptLines.length === 0) {
      toast.error('Add at least one line with a fish type and Poids (weight) greater than 0');
      return;
    }
    const totalXof = keptLines.reduce((s, l) => s + l.line_total, 0);
    const payload = {
      ...formData,
      lines: keptLines,
      exchange_rate: rate,
      subtotal: totalXof / rate,
      discount_percent: 0, discount_amount: 0, tax_percent: 0, tax_amount: 0,
      total: totalXof, total_usd: totalXof / rate, currency: 'XOF',
    };
    setSaving(true);
    try {
      if (editingInvoice) {
        await axios.put(`${API}/sales-invoices/${editingInvoice.id}`, payload);
        toast.success('Sales invoice updated');
      } else {
        await axios.post(`${API}/sales-invoices`, payload);
        toast.success('Sales invoice created');
      }
      setShowForm(false);
      fetchInvoices(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };
  
  const handlePost = async (id) => {
    if (!window.confirm('Post this invoice? This will create a voucher and update inventory.')) return;
    setPosting(true);
    try {
      const res = await axios.post(`${API}/sales-invoices/${id}/post`);
      toast.success(`Posted! Voucher: ${res.data.voucher_number}`);
      fetchInvoices(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };
  
  const handleUnpost = async (id) => {
    if (!window.confirm('Unpost this invoice? This will reverse the voucher and inventory changes.')) return;
    setPosting(true);
    try {
      await axios.post(`${API}/sales-invoices/${id}/unpost`);
      toast.success('Invoice unposted');
      fetchInvoices(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unpost');
    } finally {
      setPosting(false);
    }
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this invoice? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/sales-invoices/${id}`);
      toast.success('Invoice deleted');
      fetchInvoices(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };
  
  const buildPrintObject = (inv) => ({
    number_label: 'Facture / Invoice No',
    hide_ship: true,
    trip_number: inv.invoice_number || inv.number || '',
    date: inv.date,
    customer_account_name: inv.debit_account_name || inv.customer_name || '',
    total_facture: Number(inv.total || 0),
    invoice_lines: (inv.lines || []).map(l => ({
      fish_name: l.item_name,
      calibre_name: l.calibre_name || '',
      price: Number(l.unit_price) || 0,
      weight: Number(l.quantity) || 0,
    })),
  });

  const handlePrint = (inv) => {
    const { printInvoice } = FishingTripPrint({ trip: buildPrintObject(inv), organization: currentOrg });
    printInvoice();
  };

  const handleDownloadPdf = (inv) => {
    const { downloadInvoicePdf } = FishingTripPrint({ trip: buildPrintObject(inv), organization: currentOrg });
    downloadInvoicePdf();
  };
  
  const allInventoryItems = useMemo(() => {
    return inventoryItems.map(item => ({
      ...item,
      display_name: `${item.item_code || ''} - ${item.name}`.trim()
    }));
  }, [inventoryItems]);
  
  if (!currentOrg) return <div className="p-8 text-center text-muted-foreground">Select an organization</div>;
  
  return (
    <div className="space-y-6" data-testid="sales-invoice-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <FileText className="w-6 h-6 text-primary" />
            Sales Invoices
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Manage sales invoices and customer billing</p>
        </div>
        {canEdit() && (
          <Button onClick={openCreateForm} data-testid="create-sales-invoice-btn">
            <Plus className="w-4 h-4 mr-2" /> New Sales Invoice
          </Button>
        )}
      </div>
      
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search invoices..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-invoices-input" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]" data-testid="filter-status">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="posted">Posted</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : invoices.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No sales invoices found</p>
              {canEdit() && <Button variant="link" onClick={openCreateForm}>Create your first invoice</Button>}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="sales-invoices-table">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Invoice #</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Date</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Customer</th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground">Total (EUR)</th>
                    <th className="text-center p-3 text-xs font-medium text-muted-foreground">Status</th>
                    <th className="text-center p-3 text-xs font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-b hover:bg-muted/20 transition-colors" data-testid={`invoice-row-${inv.id}`}>
                      <td className="p-3 font-mono text-sm">{inv.invoice_number}</td>
                      <td className="p-3 text-sm">{formatDate(inv.date)}</td>
                      <td className="p-3 text-sm">
                        <span className="font-medium">{inv.debit_account_name || ''}</span>
                        <span className="text-muted-foreground ml-1 text-xs">{inv.debit_account_code || ''}</span>
                      </td>
                      <td className="p-3 text-sm text-right font-mono">{formatUSD(inv.total_usd)}</td>
                      <td className="p-3 text-center">
                        <Badge variant={inv.status === 'posted' ? 'default' : 'secondary'} className={inv.status === 'posted' ? 'bg-emerald-600' : ''}>
                          {inv.status}
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setViewInvoice(inv)} data-testid={`view-invoice-${inv.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handlePrint(inv)} data-testid={`print-invoice-${inv.id}`}>
                            <Printer className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDownloadPdf(inv)} data-testid={`pdf-invoice-${inv.id}`} title="Download PDF">
                            <Download className="w-4 h-4" />
                          </Button>
                          {!inv.is_posted && canEdit() && (
                            <>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditForm(inv)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handlePost(inv.id)} disabled={posting}>
                                <Send className="w-4 h-4 text-emerald-600" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(inv.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </>
                          )}
                          {inv.is_posted && user?.role === 'super_admin' && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleUnpost(inv.id)} disabled={posting}>
                              <Undo2 className="w-4 h-4 text-amber-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {hasMore && (
            <div className="p-4 text-center border-t">
              <Button variant="outline" onClick={() => fetchInvoices(false)} disabled={loadingMore}>
                {loadingMore ? 'Loading...' : `Load More (${invoices.length}/${totalCount})`}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-[98vw] w-full max-h-[95vh] overflow-y-auto" data-testid="sales-invoice-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingInvoice ? 'Edit Sales Invoice' : 'New Sales Invoice'}</DialogTitle>
            <DialogDescription>Fill in the invoice details</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>Date</Label>
                <DateInput value={formData.date} onChange={(val) => setFormData({ ...formData, date: val })} />
              </div>
              <div>
                <Label>Due Date</Label>
                <DateInput value={formData.due_date} onChange={(val) => setFormData({ ...formData, due_date: val })} />
              </div>
              <div>
                <Label>Customer Account (Debit)</Label>
                <AccountSelector
                  fetchUrl="customer-accounts"
                  fetchParams={{ organization_id: currentOrg.id, ...(selectedFY?.id && { fy_id: selectedFY.id }) }}
                  value={formData.debit_account_id}
                  onChange={(val) => setFormData({ ...formData, debit_account_id: val })}
                  placeholder="Search customer..."
                  quickAddType="customer"
                  organizationId={currentOrg.id}
                  onAddClick={() => { setQaCustomerMode('add'); setQaCustomerEdit(null); setQaCustomerOpen(true); }}
                  onEditClick={(acc) => { setQaCustomerMode('edit'); setQaCustomerEdit(acc); setQaCustomerOpen(true); }}
                />
              </div>
              <div>
                <Label>Sales Account (Credit)</Label>
                <AccountSelector
                  accounts={salesAccounts}
                  value={formData.credit_account_id}
                  onChange={(val) => setFormData({ ...formData, credit_account_id: val })}
                  placeholder="Select sales account..."
                  accountType="account"
                />
              </div>
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="text-base font-semibold">Line Items</Label>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={seedAllFish} data-testid="seed-fish-btn">
                    <Download className="w-4 h-4 mr-1" /> Seed all fish types
                  </Button>
                  <Button variant="outline" size="sm" onClick={addLine} data-testid="add-line-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add line
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto -mx-2 px-2">
                <table className="w-full text-sm min-w-[900px]">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left p-2 w-8">#</th>
                      <th className="text-left p-2 min-w-[200px]">Fish Type</th>
                      <th className="text-left p-2 w-[140px]">Calibre</th>
                      <th className="text-right p-2 w-[110px]">Prix/kg</th>
                      <th className="text-right p-2 w-[110px]">Cout/kg</th>
                      <th className="text-right p-2 w-[110px]">Poids (kg)</th>
                      <th className="text-right p-2 w-[130px]">Total (XOF)</th>
                      <th className="p-2 w-[40px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.lines.map((line, index) => (
                      <tr key={index} className="border-b" data-testid={`line-row-${index}`}>
                        <td className="p-2 text-center text-muted-foreground">{index + 1}</td>
                        <td className="p-2">
                          <Select value={line.inventory_item_id || ''} onValueChange={(val) => handleFishSelect(index, val)}>
                            <SelectTrigger className="h-9" data-testid={`fish-select-${index}`}><SelectValue placeholder="Select fish..." /></SelectTrigger>
                            <SelectContent>
                              {inventoryItems.map(it => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-2">
                          <Select value={line.calibre_name || ''} onValueChange={(val) => handleLineChange(index, 'calibre_name', val)}>
                            <SelectTrigger className="h-9" data-testid={`calibre-select-${index}`}><SelectValue placeholder="Calibre" /></SelectTrigger>
                            <SelectContent>
                              {calibres.map(c => <SelectItem key={c.id || c.name} value={c.name}>{c.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-2">
                          <Input type="number" value={line.unit_price} placeholder="0" onChange={(e) => handleLineChange(index, 'unit_price', e.target.value)} min="0" className="h-9 text-right" data-testid={`price-${index}`} />
                        </td>
                        <td className="p-2">
                          <Input type="number" value={line.cost} placeholder="0" onChange={(e) => handleLineChange(index, 'cost', e.target.value)} min="0" className="h-9 text-right" data-testid={`cost-${index}`} />
                        </td>
                        <td className="p-2">
                          <Input type="number" value={line.quantity} placeholder="0" onChange={(e) => handleLineChange(index, 'quantity', e.target.value)} min="0" className="h-9 text-right" data-testid={`weight-${index}`} />
                        </td>
                        <td className="p-2 text-right font-mono text-primary">{fmt(line.line_total || 0)}</td>
                        <td className="p-2">
                          {formData.lines.length > 1 && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeLine(index)}>
                              <X className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div>
                  <Label>Notes</Label>
                  <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional notes..." rows={3} />
                </div>
              </div>
              <div className="space-y-2 bg-muted/30 rounded-lg p-4">
                <div className="flex justify-between text-sm">
                  <span>Subtotal (XOF)</span>
                  <span className="font-mono">{fmt(formData.total)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-bold text-lg">
                  <span>Total (XOF)</span>
                  <span className="font-mono text-primary" data-testid="invoice-total">{fmt(formData.total)}</span>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-invoice-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : (editingInvoice ? 'Update Invoice' : 'Create Invoice')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* View Dialog */}
      <Dialog open={!!viewInvoice} onOpenChange={() => setViewInvoice(null)}>
        <DialogContent className="max-w-[95vw] w-full max-h-[90vh] overflow-y-auto" data-testid="view-invoice-dialog">
          {viewInvoice && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  Sales Invoice: {viewInvoice.invoice_number}
                  <Badge variant={viewInvoice.status === 'posted' ? 'default' : 'secondary'} className={viewInvoice.status === 'posted' ? 'bg-emerald-600 ml-2' : 'ml-2'}>
                    {viewInvoice.status}
                  </Badge>
                </DialogTitle>
                <DialogDescription>Invoice details and line items</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div><span className="text-muted-foreground">Date:</span> <strong>{formatDate(viewInvoice.date)}</strong></div>
                  <div><span className="text-muted-foreground">Customer:</span> <strong>{viewInvoice.debit_account_name} ({viewInvoice.debit_account_code})</strong></div>
                  <div><span className="text-muted-foreground">Sales Acc:</span> <strong>{viewInvoice.credit_account_name} ({viewInvoice.credit_account_code})</strong></div>
                  <div><span className="text-muted-foreground">Total:</span> <strong className="text-primary">{formatUSD(viewInvoice.total_usd)}</strong></div>
                  {viewInvoice.voucher_number && <div><span className="text-muted-foreground">Voucher:</span> <strong>{viewInvoice.voucher_number}</strong></div>}
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left p-2">Item</th>
                      <th className="text-right p-2">Qty</th>
                      <th className="text-right p-2">Price</th>
                      <th className="text-right p-2">Disc%</th>
                      <th className="text-right p-2">Total EUR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewInvoice.lines.map((line, i) => (
                      <tr key={i} className="border-b">
                        <td className="p-2">{line.item_name}</td>
                        <td className="p-2 text-right">{line.quantity} {line.unit}</td>
                        <td className="p-2 text-right">{line.unit_price} {line.currency}</td>
                        <td className="p-2 text-right">{line.discount_percent||0}%</td>
                        <td className="p-2 text-right font-mono">{formatUSD(line.line_total_usd || line.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => handleDownloadPdf(viewInvoice)} data-testid="pdf-from-view-btn">
                    <Download className="w-4 h-4 mr-2" /> Download PDF
                  </Button>
                  <Button onClick={() => handlePrint(viewInvoice)} data-testid="print-from-view-btn">
                    <Printer className="w-4 h-4 mr-2" /> Print Invoice
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <QuickAddAccountDialog open={qaCustomerOpen} onOpenChange={setQaCustomerOpen}
        type="customer" mode={qaCustomerMode} editData={qaCustomerEdit}
        organizationId={currentOrg?.id} onSaved={() => fetchData()} />
      <QuickAddInventoryDialog open={qaItemOpen} onOpenChange={setQaItemOpen}
        mode={qaItemMode} editData={qaItemEdit}
        organizationId={currentOrg?.id} onSaved={() => fetchData()} />
    </div>
  );
};

export default SalesInvoicePage;
